import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import { useFormReadOnly } from '../../../../../common';

import type { AppDispatch } from '../../../../../../store';
import type { TreatmentPlanV3MedicalInfoPayload, TreatmentPlanV3MedicalDiagnosisPayload } from '../../../../../../store/treatmentPlanV3';

export default function useTreatmentPlanV3Medical() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const readOnly = useFormReadOnly();
  const updateInfo = useCallback(async (data: TreatmentPlanV3MedicalInfoPayload) => {
    if (!treatmentPlanId || readOnly) return false;
    const result = await dispatch(treatmentPlanV3Actions.updateTreatmentPlanV3MedicalInfo({ treatmentPlanId, data, getAccessTokenSilently }));
    return treatmentPlanV3Actions.updateTreatmentPlanV3MedicalInfo.fulfilled.match(result);
  }, [dispatch, getAccessTokenSilently, treatmentPlanId, readOnly]);
  const createDiagnosis = useCallback(async (data: TreatmentPlanV3MedicalDiagnosisPayload) => {
    if (!treatmentPlanId || readOnly) return undefined;
    const result = await dispatch(treatmentPlanV3Actions.createTreatmentPlanV3MedicalDiagnosis({ treatmentPlanId, data, getAccessTokenSilently }));
    return treatmentPlanV3Actions.createTreatmentPlanV3MedicalDiagnosis.fulfilled.match(result) ? result.payload.id : undefined;
  }, [dispatch, getAccessTokenSilently, treatmentPlanId, readOnly]);
  const updateDiagnosis = useCallback(async (diagnosisId: number, data: TreatmentPlanV3MedicalDiagnosisPayload) => {
    if (!treatmentPlanId || readOnly) return false;
    const result = await dispatch(treatmentPlanV3Actions.updateTreatmentPlanV3MedicalDiagnosis({ treatmentPlanId, diagnosisId, data, getAccessTokenSilently }));
    return treatmentPlanV3Actions.updateTreatmentPlanV3MedicalDiagnosis.fulfilled.match(result);
  }, [dispatch, getAccessTokenSilently, treatmentPlanId, readOnly]);
  const deleteDiagnosis = useCallback(async (diagnosisId: number) => {
    if (!treatmentPlanId || readOnly) return false;
    const result = await dispatch(treatmentPlanV3Actions.deleteTreatmentPlanV3MedicalDiagnosis({ treatmentPlanId, diagnosisId, getAccessTokenSilently }));
    return treatmentPlanV3Actions.deleteTreatmentPlanV3MedicalDiagnosis.fulfilled.match(result);
  }, [dispatch, getAccessTokenSilently, treatmentPlanId, readOnly]);
  const createSelection = useCallback(async (selection_type: string, value: string) => {
    if (!treatmentPlanId || readOnly) return false;
    const result = await dispatch(treatmentPlanV3Actions.createTreatmentPlanV3MedicalSelection({ treatmentPlanId, data: { selection_type, value }, getAccessTokenSilently }));
    return treatmentPlanV3Actions.createTreatmentPlanV3MedicalSelection.fulfilled.match(result);
  }, [dispatch, getAccessTokenSilently, treatmentPlanId, readOnly]);
  const deleteSelection = useCallback(async (selectionType: string, selectionId: number) => {
    if (!treatmentPlanId || readOnly) return false;
    const result = await dispatch(treatmentPlanV3Actions.deleteTreatmentPlanV3MedicalSelection({ treatmentPlanId, selectionType, selectionId, getAccessTokenSilently }));
    return treatmentPlanV3Actions.deleteTreatmentPlanV3MedicalSelection.fulfilled.match(result);
  }, [dispatch, getAccessTokenSilently, treatmentPlanId, readOnly]);
  return { updateInfo, createDiagnosis, updateDiagnosis, deleteDiagnosis, createSelection, deleteSelection };
}
